import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";

const CartContext = createContext(null);
const KEY = "sapore_cart_v1";

function lineKey(item_id, variant, addons) {
  const a = (addons || []).map((x) => x.id).sort().join(",");
  return `${item_id}__${variant || ""}__${a}`;
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(KEY)) || [];
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(items));
  }, [items]);

  const addItem = useCallback((line) => {
    const key = lineKey(line.item_id, line.variant, line.addons);
    setItems((prev) => {
      const existing = prev.find((p) => p.key === key);
      if (existing) {
        return prev.map((p) => (p.key === key ? { ...p, quantity: p.quantity + line.quantity } : p));
      }
      return [...prev, { ...line, key }];
    });
    setOpen(true);
  }, []);

  const removeItem = useCallback((key) => setItems((prev) => prev.filter((p) => p.key !== key)), []);

  const updateQty = useCallback((key, quantity) => {
    if (quantity < 1) return removeItem(key);
    setItems((prev) => prev.map((p) => (p.key === key ? { ...p, quantity } : p)));
  }, [removeItem]);

  const clear = useCallback(() => setItems([]), []);

  const lineTotal = useCallback((p) => (p.unit_price + (p.addons || []).reduce((s, a) => s + a.price, 0)) * p.quantity, []);

  const subtotal = useMemo(() => items.reduce((s, p) => s + lineTotal(p), 0), [items, lineTotal]);
  const count = useMemo(() => items.reduce((s, p) => s + p.quantity, 0), [items]);

  const value = useMemo(
    () => ({ items, addItem, updateQty, removeItem, clear, subtotal, count, lineTotal, open, setOpen }),
    [items, addItem, updateQty, removeItem, clear, subtotal, count, lineTotal, open]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => useContext(CartContext);
